import React from 'react';
import {
  Smartphone,
  Globe,
  Users,
  Copy,
  ExternalLink,
  Play,
  CheckCircle,
  Lock,
  Apple,
  Share2,
  Eye,
  QrCode,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';

/**
 * Portals & Mobile App Section
 * 
 * Landing page with cards for:
 * - Client Portal
 * - Affiliate Portal
 * - Mobile App
 * 
 * Each card shows status, quick actions (copy link, preview, share).
 * This is NOT a multi-level submenu - it's a card-based landing page.
 */
const PortalsMobileAppSection = ({ agency, agencyId, entitlements, isReadOnly }) => {
  
  // Generate portal URLs
  const clientPortalUrl = `https://${agencyId}.fundelite.com/portal`;
  const affiliatePortalUrl = `https://${agencyId}.fundelite.com/affiliate`;
  const mobileAppUrl = `https://app.fundelite.com/download`;
  
  // Check entitlement status
  const isAffiliateEnabled = entitlements?.affiliate_portal === 'full_access';
  const isMobileAppEnabled = entitlements?.mobile_app !== 'hidden';
  
  // Copy to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };
  
  return (
    <div className="space-y-6" data-testid="portals-mobile-section">
      {/* Section Header */}
      <div className="border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
            <Smartphone className="text-indigo-600" size={20} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Portals & Mobile App</h2>
            <p className="text-sm text-slate-500">
              Manage client portal, affiliate portal, and mobile app access
            </p>
          </div>
        </div>
      </div>
      
      {/* Portal Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Client Portal Card */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-amber-200 hover:shadow-sm transition-all">
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                <Globe className="text-white" size={24} />
              </div>
              <Badge className="bg-emerald-100 text-emerald-700 border-0">
                <CheckCircle size={12} className="mr-1" />
                Active
              </Badge>
            </div>
            
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Client Portal</h3>
            <p className="text-sm text-slate-500 mb-4">
              Your branded client-facing portal for credit repair services
            </p>
            
            {/* Portal URL */}
            <div className="bg-slate-50 rounded-lg p-3 mb-4">
              <p className="text-xs text-slate-400 mb-1">Portal URL</p>
              <p className="text-sm font-mono text-slate-700 truncate">{clientPortalUrl}</p>
            </div>
            
            {/* Actions */}
            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => copyToClipboard(clientPortalUrl, 'Portal link')}
              >
                <Copy size={16} className="mr-2" />
                Copy Portal Link
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => window.open(clientPortalUrl, '_blank')}
              >
                <ExternalLink size={16} className="mr-2" />
                Open Preview
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start text-slate-500"
              >
                <Play size={16} className="mr-2" />
                Watch Video Tour
              </Button>
            </div>
          </div>
        </div>
        
        {/* Affiliate Portal Card */}
        <div className={`bg-white rounded-xl border overflow-hidden transition-all ${
          isAffiliateEnabled 
            ? 'border-slate-200 hover:border-amber-200 hover:shadow-sm' 
            : 'border-slate-100 opacity-80'
        }`}>
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                isAffiliateEnabled 
                  ? 'bg-gradient-to-br from-blue-500 to-blue-600' 
                  : 'bg-slate-200'
              }`}>
                <Users className={isAffiliateEnabled ? 'text-white' : 'text-slate-400'} size={24} />
              </div>
              {isAffiliateEnabled ? (
                <Badge className="bg-emerald-100 text-emerald-700 border-0">
                  <CheckCircle size={12} className="mr-1" />
                  Active
                </Badge>
              ) : (
                <Badge className="bg-slate-100 text-slate-500 border-0">
                  <Lock size={12} className="mr-1" />
                  Locked
                </Badge>
              )}
            </div>
            
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Affiliate Portal</h3>
            <p className="text-sm text-slate-500 mb-4">
              Partner and affiliate referral management portal
            </p>
            
            {isAffiliateEnabled ? (
              <>
                {/* Portal URL */}
                <div className="bg-slate-50 rounded-lg p-3 mb-4">
                  <p className="text-xs text-slate-400 mb-1">Affiliate URL</p>
                  <p className="text-sm font-mono text-slate-700 truncate">{affiliatePortalUrl}</p>
                </div>
                
                {/* Actions */}
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => copyToClipboard(affiliatePortalUrl, 'Affiliate link')}
                  >
                    <Copy size={16} className="mr-2" />
                    Copy Affiliate Link
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => window.open(affiliatePortalUrl, '_blank')}
                  >
                    <ExternalLink size={16} className="mr-2" />
                    Open Preview
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-slate-500 mb-3">
                  Affiliate portal requires a plan upgrade
                </p>
                <Button
                  variant="outline"
                  className="text-amber-600 border-amber-200 hover:bg-amber-50"
                >
                  Upgrade / Add Features
                </Button>
              </div>
            )}
          </div>
        </div>
        
        {/* Mobile App Card */}
        <div className={`bg-white rounded-xl border overflow-hidden transition-all ${
          isMobileAppEnabled 
            ? 'border-slate-200 hover:border-amber-200 hover:shadow-sm' 
            : 'border-slate-100 opacity-80'
        }`}>
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                isMobileAppEnabled 
                  ? 'bg-gradient-to-br from-purple-500 to-purple-600' 
                  : 'bg-slate-200'
              }`}>
                <Smartphone className={isMobileAppEnabled ? 'text-white' : 'text-slate-400'} size={24} />
              </div>
              {isMobileAppEnabled ? (
                <Badge className="bg-blue-100 text-blue-700 border-0">
                  <Eye size={12} className="mr-1" />
                  Beta
                </Badge>
              ) : (
                <Badge className="bg-slate-100 text-slate-500 border-0">
                  <Lock size={12} className="mr-1" />
                  Coming Soon
                </Badge>
              )}
            </div>
            
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Mobile App</h3>
            <p className="text-sm text-slate-500 mb-4">
              Native iOS and Android app for your clients
            </p>
            
            {isMobileAppEnabled ? (
              <>
                {/* App Store Links */}
                <div className="flex gap-3 mb-4">
                  <Button variant="outline" className="flex-1 justify-center" size="sm">
                    <Apple size={16} className="mr-1" />
                    iOS
                  </Button>
                  <Button variant="outline" className="flex-1 justify-center" size="sm">
                    <Smartphone size={16} className="mr-1" />
                    Android
                  </Button>
                </div>
                
                {/* Actions */}
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => copyToClipboard(mobileAppUrl, 'App link')}
                  >
                    <Share2 size={16} className="mr-2" />
                    Share Download Link
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                  >
                    <QrCode size={16} className="mr-2" />
                    Generate QR Code
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-slate-500 mb-3">
                  Mobile app access coming soon
                </p>
                <Button
                  variant="outline"
                  className="text-slate-400"
                  disabled
                >
                  Join Waitlist
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Quick Tips */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <h4 className="font-semibold text-amber-800 mb-2">Portal Tips</h4>
        <ul className="text-sm text-amber-700 space-y-1">
          <li>• Share your client portal link in emails and social media to drive sign-ups</li>
          <li>• Customize portal branding in Settings → Branding & White Label</li>
          <li>• Set up a custom domain for a professional branded experience</li>
        </ul>
      </div>
    </div>
  );
};

export default PortalsMobileAppSection;
